import React, { useState, useMemo, useEffect } from 'react';
import { TrendingUp, Calendar, BarChart3, Download, RefreshCw, ChevronDown, ChevronRight, ChevronLeft, ChevronFirst, ChevronLast, Eye } from 'lucide-react';
import { format, addMonths, addYears, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';

const AggregateDataTable = ({ policies }) => {
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'yearly'
  const [selectedCurrency, setSelectedCurrency] = useState('all');
  const [displayCurrency, setDisplayCurrency] = useState('JPY'); // Display currency
  const [exchangeRates, setExchangeRates] = useState({});
  const [ratesLoading, setRatesLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded rows
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Items per page

  const generatePaymentSchedule = (policy) => {
    const schedule = [];
    const startDate = parseISO(policy.effective_start_date);
    const endDate = policy.effective_end_date ? parseISO(policy.effective_end_date) : null;
    
    let currentDate = startDate;
    let paymentCount = 0;
    
    while (paymentCount < policy.total_period && (!endDate || currentDate <= endDate)) {
      const payment = {
        date: currentDate,
        amount: policy.premium_amount,
        period: paymentCount + 1,
        policy_number: policy.policy_number,
        currency: policy.currency
      };
      
      schedule.push(payment);
      
      if (policy.premium_frequency === 'monthly') {
        currentDate = addMonths(currentDate, 1);
      } else {
        currentDate = addYears(currentDate, 1);
      }
      
      paymentCount++;
    }
    
    return schedule;
  };

  // Fetch exchange rates from a free API
  const fetchExchangeRates = async () => {
    setRatesLoading(true);
    try {
      // Using exchangerate-api.com (free tier)
      const response = await fetch(`https://api.exchangerate-api.com/v4/latest/USD`);
      const data = await response.json();
      
      // Set rates with USD as base
      const rates = {
        USD: 1,
        JPY: data.rates.JPY || 150, // Fallback rate
        EUR: data.rates.EUR || 0.85, // Fallback rate
      };
      
      setExchangeRates(rates);
    } catch (error) {
      console.warn('Failed to fetch exchange rates, using fallback rates');
      // Fallback exchange rates
      setExchangeRates({
        USD: 1,
        JPY: 150,
        EUR: 0.85,
      });
    } finally {
      setRatesLoading(false);
    }
  };

  // Fetch exchange rates on component mount and when display currency changes
  useEffect(() => {
    fetchExchangeRates();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCurrency, displayCurrency]);

  // Convert amount from source currency to display currency
  const convertCurrency = (amount, fromCurrency, toCurrency = displayCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    
    if (!exchangeRates.USD) return amount; // No rates available
    
    // Convert to USD first, then to target currency
    let usdAmount;
    if (fromCurrency === 'USD') {
      usdAmount = amount;
    } else {
      usdAmount = amount / (exchangeRates[fromCurrency] || 1);
    }
    
    // Convert from USD to target currency
    if (toCurrency === 'USD') {
      return usdAmount;
    } else {
      return usdAmount * (exchangeRates[toCurrency] || 1);
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (period) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(period)) {
      newExpandedRows.delete(period);
    } else {
      newExpandedRows.add(period);
    }
    setExpandedRows(newExpandedRows);
  };

  const currencies = useMemo(() => {
    const uniqueCurrencies = [...new Set(policies.map(p => p.currency))];
    return ['all', ...uniqueCurrencies];
  }, [policies]);

  const aggregateData = useMemo(() => {
    if (policies.length === 0) return {};

    const aggregated = {};
    
    policies.forEach(policy => {
      if (selectedCurrency !== 'all' && policy.currency !== selectedCurrency) return;
      
      const schedule = generatePaymentSchedule(policy);
      
      schedule.forEach(payment => {
        let key;
        if (viewMode === 'monthly') {
          key = format(payment.date, 'yyyy-MM');
        } else {
          key = format(payment.date, 'yyyy');
        }
        
        if (!aggregated[key]) {
          aggregated[key] = {
            total: 0,
            count: 0,
            policies: new Set(),
            policyDetails: [], // Store actual policy objects
            currencies: {},
            originalCurrencies: {} // Store original amounts in original currencies
          };
        }
        
        const convertedAmount = convertCurrency(payment.amount, policy.currency);
        
        aggregated[key].total += convertedAmount;
        aggregated[key].count += 1;
        aggregated[key].policies.add(policy.policy_number);
        
        // Add policy details if not already present
        if (!aggregated[key].policyDetails.find(p => p.policy_number === policy.policy_number)) {
          aggregated[key].policyDetails.push({
            ...policy,
            convertedAmount: convertCurrency(policy.premium_amount, policy.currency)
          });
        }
        
        if (!aggregated[key].currencies[policy.currency]) {
          aggregated[key].currencies[policy.currency] = 0;
          aggregated[key].originalCurrencies[policy.currency] = 0;
        }
        aggregated[key].currencies[policy.currency] += convertedAmount;
        aggregated[key].originalCurrencies[policy.currency] += payment.amount; // Store original amount
      });
    });
    
    return aggregated;
  }, [policies, viewMode, selectedCurrency, convertCurrency]);

  const formatCurrency = (amount, currency = displayCurrency) => {
    const locale = currency === 'JPY' ? 'ja-JP' : currency === 'EUR' ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: currency === 'JPY' ? 0 : 2,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    }).format(amount);
  };

  const calculateTotals = () => {
    const totals = {
      totalRevenue: 0,
      totalPayments: 0,
      totalPolicies: new Set(),
      currencyBreakdown: {},
      originalCurrencyBreakdown: {} // Track original amounts in original currencies
    };

    Object.values(aggregateData).forEach(period => {
      totals.totalRevenue += period.total;
      totals.totalPayments += period.count;
      period.policies.forEach(policy => totals.totalPolicies.add(policy));
      
      Object.entries(period.currencies).forEach(([currency, amount]) => {
        if (!totals.currencyBreakdown[currency]) {
          totals.currencyBreakdown[currency] = 0;
          totals.originalCurrencyBreakdown[currency] = 0;
        }
        totals.currencyBreakdown[currency] += amount;
        totals.originalCurrencyBreakdown[currency] += period.originalCurrencies[currency];
      });
    });

    return totals;
  };

  const totals = calculateTotals();

  const sortedPeriods = Object.keys(aggregateData).sort((a, b) => a.localeCompare(b));

  // Get currency icon based on selected currency
  const getCurrencyIcon = (currency) => {
    switch (currency) {
      case 'JPY':
        return '¥';
      case 'EUR':
        return '€';
      case 'USD':
      default:
        return '$';
    }
  };

  // Pagination logic
  const totalItems = sortedPeriods.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPeriods = sortedPeriods.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">View Mode:</span>
          <div className="flex bg-quickbooks-gray rounded-lg p-1">
            <button
              onClick={() => setViewMode('monthly')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                viewMode === 'monthly'
                  ? 'bg-enpact-red text-white shadow-quickbooks'
                  : 'text-quickbooks-gray-dark hover:text-enpact-red'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setViewMode('yearly')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                viewMode === 'yearly'
                  ? 'bg-enpact-red text-white shadow-quickbooks'
                  : 'text-quickbooks-gray-dark hover:text-enpact-red'
              }`}
            >
              Yearly
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Policy Filter:</span>
          <select
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            className="input-field w-auto"
          >
            {currencies.map(currency => (
              <option key={currency} value={currency}>
                {currency === 'all' ? 'All Policies' : `${currency} Policies`}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Display Currency:</span>
          <div className="flex items-center space-x-2">
            <select
              value={displayCurrency}
              onChange={(e) => setDisplayCurrency(e.target.value)}
              className="input-field w-auto"
            >
              <option value="JPY">Japanese Yen (¥)</option>
              <option value="USD">US Dollar ($)</option>
              <option value="EUR">Euro (€)</option>
            </select>
            <button
              onClick={fetchExchangeRates}
              disabled={ratesLoading}
              className="p-2 bg-enpact-red text-white rounded-md hover:bg-enpact-red-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              title="Refresh exchange rates"
            >
              <RefreshCw className={`h-4 w-4 ${ratesLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="card bg-gradient-to-r from-enpact-red to-enpact-red-dark text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Total Revenue</p>
              <p className="text-2xl font-bold">
                {formatCurrency(totals.totalRevenue, displayCurrency)}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 opacity-80" />
          </div>
        </div>

        <div className="card bg-gradient-to-r from-gray-500 to-gray-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Total Payments</p>
              <p className="text-2xl font-bold">{totals.totalPayments.toLocaleString()}</p>
            </div>
            <div className="text-3xl font-bold opacity-80">
              {getCurrencyIcon(displayCurrency)}
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-r from-enpact-red to-enpact-red-dark text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Active Policies</p>
              <p className="text-2xl font-bold">{totals.totalPolicies.size}</p>
            </div>
            <BarChart3 className="h-8 w-8 opacity-80" />
          </div>
        </div>

        <div className="card bg-gradient-to-r from-gray-500 to-gray-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Time Periods</p>
              <p className="text-2xl font-bold">{sortedPeriods.length}</p>
            </div>
            <Calendar className="h-8 w-8 opacity-80" />
          </div>
        </div>
      </div>

      {/* Currency Breakdown */}
      {Object.keys(totals.currencyBreakdown).length > 1 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Currency Breakdown</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(totals.currencyBreakdown).map(([currency, amount]) => (
              <div key={currency} className="p-4 bg-quickbooks-gray rounded-lg">
                <div className="text-sm text-quickbooks-gray-dark">{currency}</div>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(totals.originalCurrencyBreakdown[currency], currency)}
                </div>
                <div className="text-xs text-gray-500">
                  (Converted: {formatCurrency(amount, displayCurrency)})
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Aggregate Data Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {viewMode === 'monthly' ? 'Monthly' : 'Yearly'} Revenue Summary
          </h3>
          <button className="btn-primary flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-quickbooks-gray border-b border-quickbooks-border">
                <th className="text-left p-3 font-semibold text-gray-900 w-8">
                  {/* Expand/Collapse */}
                </th>
                <th className="text-left p-3 font-semibold text-gray-900">
                  {viewMode === 'monthly' ? 'Month' : 'Year'}
                </th>
                <th className="text-left p-3 font-semibold text-gray-900">Total Revenue</th>
                <th className="text-left p-3 font-semibold text-gray-900">Payment Count</th>
                <th className="text-left p-3 font-semibold text-gray-900">Active Policies</th>
                <th className="text-left p-3 font-semibold text-gray-900">Currency Breakdown</th>
              </tr>
            </thead>
            <tbody>
              {currentPeriods.map((period) => {
                const data = aggregateData[period];
                const isExpanded = expandedRows.has(period);
                return (
                  <React.Fragment key={period}>
                    <tr className="border-b border-quickbooks-border hover:bg-quickbooks-gray-light">
                      <td className="p-3">
                        <button
                          onClick={() => toggleRowExpansion(period)}
                          className="flex items-center justify-center w-6 h-6 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="p-3 font-medium text-gray-900">
                        {viewMode === 'monthly' 
                          ? format(parseISO(period + '-01'), 'MMM yyyy')
                          : period
                        }
                      </td>
                      <td className="p-3 font-semibold text-gray-900">
                        {formatCurrency(data.total, displayCurrency)}
                      </td>
                      <td className="p-3 text-gray-700">{data.count}</td>
                      <td className="p-3 text-gray-700">{data.policies.size}</td>
                      <td className="p-3">
                        <div className="space-y-1">
                          {Object.entries(data.currencies).map(([currency, amount]) => (
                            <div key={currency} className="text-sm">
                              <span className="font-medium">{currency}:</span>{' '}
                              <span className="text-gray-700">
                                {formatCurrency(data.originalCurrencies[currency], currency)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50 border-b border-quickbooks-border">
                        <td colSpan="6" className="p-0">
                          <div className="p-4 border-l-4 border-enpact-red">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              Policies for {viewMode === 'monthly' 
                                ? format(parseISO(period + '-01'), 'MMM yyyy')
                                : period
                              }
                            </h4>
                            <div className="grid gap-3">
                              {data.policyDetails.map((policy) => (
                                <div key={policy.policy_number} className="bg-white p-3 rounded-md border border-gray-200">
                                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                                    <div>
                                      <span className="font-medium text-gray-700">Policy:</span>
                                      <div className="text-gray-900">{policy.policy_number}</div>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Policyholder:</span>
                                      <div className="text-gray-900">{policy.policyholder_name}</div>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Premium Amount:</span>
                                      <div className="text-gray-900 font-medium">
                                        {formatCurrency(policy.convertedAmount, displayCurrency)}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        ({policy.premium_frequency})
                                      </div>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Original Currency:</span>
                                      <div className="text-gray-900">{policy.currency}</div>
                                    </div>
                                  </div>
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <Link
                                      to={`/policies/${policy.policy_number}`}
                                      className="inline-flex items-center px-3 py-1 bg-enpact-red text-white text-xs font-medium rounded-md hover:bg-enpact-red-dark transition-colors duration-200"
                                    >
                                      <Eye className="h-3 w-3 mr-1" />
                                      View Details
                                    </Link>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                title="First page"
              >
                <ChevronFirst className="h-4 w-4" />
              </button>
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                        currentPage === pageNum
                          ? 'bg-enpact-red text-white'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                title="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                title="Last page"
              >
                <ChevronLast className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AggregateDataTable;
